import { useCallback, useEffect, useState } from "react";
import { AlertTriangle, ExternalLink, Loader2, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { authFetch } from "@/lib/api";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const BILLING_URL = "https://platform.openai.com/settings/organization/billing/overview";

const CAUSES = {
  "IA-CREDIT": {
    cause: "Le crédit du compte OpenAI est épuisé.",
    action: "Rechargez le crédit sur OpenAI (pensez à la recharge automatique), puis vérifiez.",
  },
  "IA-CLE": {
    cause: "La clé API OpenAI est absente ou refusée.",
    action: "Enregistrez une clé valide dans l'onglet « Clé API », puis vérifiez.",
  },
};

const fmt = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-FR", {
    timeZone: "Indian/Reunion", day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit",
  });
};

/** Bandeau de l'espace admin quand les fonctions IA sont hors service (crédit OpenAI épuisé, clé refusée). */
export default function AiStatusBanner({ canCheck, onOpenKeyTab }) {
  const [status, setStatus] = useState(null);
  const [checking, setChecking] = useState(false);

  const refresh = useCallback(async () => {
    try {
      const res = await authFetch(`${API_BASE}/admin/ai-status`);
      if (res.ok) setStatus(await res.json());
    } catch { /* réseau : on garde le dernier état connu */ }
  }, []);

  useEffect(() => {
    refresh();
    const timer = setInterval(refresh, 60000);
    return () => clearInterval(timer);
  }, [refresh]);

  const check = async () => {
    setChecking(true);
    try {
      const res = await authFetch(`${API_BASE}/admin/ai-config/test`, { method: "POST" });
      const data = await res.json().catch(() => ({}));
      if (res.ok && data.ok) toast.success("Fonctions IA rétablies");
      else toast.error("Toujours indisponible : " + (data.message || data.detail || "échec du test"));
    } catch { toast.error("Erreur lors de la vérification"); }
    await refresh();
    setChecking(false);
  };

  if (status?.state !== "indisponible") return null;
  const info = CAUSES[status.code] || CAUSES["IA-CREDIT"];

  return (
    <div className="px-4 pt-4" data-testid="ai-status-banner">
      <div className="max-w-5xl mx-auto rounded-xl border p-4 flex flex-col sm:flex-row sm:items-center gap-3"
        style={{ background: "#FEF3F2", borderColor: "#FECDCA" }} role="alert">
        <AlertTriangle className="w-5 h-5 shrink-0" style={{ color: "#B42318" }} />
        <div className="flex-1 min-w-0">
          <p className="text-sm font-bold" style={{ color: "#7A271A" }}>Fonctions IA indisponibles pour tous les utilisateurs</p>
          <p className="text-xs mt-0.5" style={{ color: "#4A4A4A" }}>
            {info.cause} Depuis le {fmt(status.since)} · {status.count || 1} demande{(status.count || 1) > 1 ? "s" : ""} refusée{(status.count || 1) > 1 ? "s" : ""} · code {status.code}
          </p>
          <p className="text-xs mt-0.5" style={{ color: "#4A4A4A" }}>
            {info.action}{status.notified_at ? " Les super-administrateurs ont été prévenus par e-mail." : ""}
          </p>
        </div>
        <div className="flex flex-wrap gap-2 shrink-0">
          {status.code === "IA-CREDIT" ? (
            <a href={BILLING_URL} target="_blank" rel="noopener noreferrer"
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold text-white"
              style={{ background: "#B42318" }} data-testid="ai-status-billing">
              Recharger le crédit <ExternalLink className="w-3.5 h-3.5" />
            </a>
          ) : (
            <button onClick={onOpenKeyTab} className="h-9 px-3 rounded-lg text-xs font-semibold text-white"
              style={{ background: "#B42318" }} data-testid="ai-status-key-tab">
              Onglet Clé API
            </button>
          )}
          {canCheck && (
            <button onClick={check} disabled={checking}
              className="inline-flex items-center gap-1.5 h-9 px-3 rounded-lg text-xs font-semibold border bg-white disabled:opacity-60"
              style={{ borderColor: "#FECDCA", color: "#7A271A" }} data-testid="ai-status-check">
              {checking ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              Vérifier maintenant
            </button>
          )}
        </div>
      </div>
    </div>
  );
}
